import React, { useMemo, useState } from "react";
import CriteriaHelpModal from './components/CriteriaHelpModal';
import { 
  refFamille, 
  refSousType, 
  refNbFrappes, 
  refResultat,
  refEffet,
  refFauteDirecte,
  refServiceReussite, 
  refZone
} from "./data/refs";

function ToggleButton({ active, onClick, children, small = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: small ? "2px 4px" : "5px 5px",
        borderRadius: "999px",
        border: active ? "1px solid #1565c0" : "1px solid #ccc",
        backgroundColor: active ? "#1976d2" : "#f5f5f5",
        color: active ? "#fff" : "#333",
        fontSize: small ? "13px" : "13px",
        fontWeight: active ? 600 : 400,
        cursor: "pointer",
        whiteSpace: "nowrap",
        alignSelf: "flex-start",
      }}
    >
      {children}
    </button>
  );
}

  function PointForm({ value, onChange, dynamicSousTypes = null, dynamicZones = null, resetForm }) {
    const [showAdvanced, setShowAdvanced] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

    const pointForm = value || {
      familleCode: "",
      sousTypeCode: "",
      nbFrappesCode: "",
      resultatCode: null,
      serviceReussite: "",
      zoneCode: "",
      effetCode: "",
      fauteDirecteCode: "",
    };

    // 🆕 Détecter si on est en mode Point libre
    const isPointLibre = pointForm.familleCode === "?";

    React.useEffect(() => {
      if (resetForm !== undefined) {
        console.log("🔄 PointForm FORCE RESET");
        
        const emptyForm = {
          familleCode: "",
          sousTypeCode: "",
          nbFrappesCode: "",
          resultatCode: null,
          serviceReussite: "",
          zoneCode: "",
          effetCode: "",
          fauteDirecteCode: ""
        };
        
        onChange(emptyForm);
      }
    }, [resetForm, onChange]);
    
  const sousTypes = useMemo(() => {
    // 🆕 Pas de sous-type pour le point libre
    if (isPointLibre) return [];

    // 1️⃣ Si la page live fournit une liste dynamique (3 sous-types service)
    if (dynamicSousTypes && dynamicSousTypes.length > 0) {
      return dynamicSousTypes;
    }

    // 2️⃣ Sinon, fallback sur la ref complète
    if (pointForm.familleCode) {
      return refSousType[pointForm.familleCode] || [];
    }

    return [];
  }, [pointForm.familleCode, dynamicSousTypes, isPointLibre]);

  const updateField = (field, newValue) => {
    console.log(`🔍 UPDATE ${field} = "${newValue}"`);
    let updated = { ...pointForm, [field]: newValue };
    
    // 🆕 Si on sélectionne le Point libre "?"
    // → Reset tous les champs inutiles + Résultat = "Point" automatiquement
    if (field === "familleCode" && newValue === "?") {
      updated = {
        ...updated,
        familleCode: "?",
        sousTypeCode: "",
        nbFrappesCode: "",
        resultatCode: "",   // "" = Point (pas de faute)
        serviceReussite: "",
        zoneCode: "",
        effetCode: "",
        fauteDirecteCode: "",
      };
      console.log("🆓 Point libre sélectionné → Résultat = Point automatiquement");
      onChange(updated);
      return;
    }

    // 🆕 Si on quitte le Point libre vers une autre famille → reset résultat
    if (field === "familleCode" && pointForm.familleCode === "?" && newValue !== "?") {
      updated.resultatCode = null;
    }
    
    // RESET SOUS-TYPE si famille change (SAUF pour Retour avec zone service)
    if (field === "familleCode" && newValue !== pointForm.familleCode) {
      if (["S", "T"].includes(newValue)) {
        updated.nbFrappesCode = "";
        console.log("🔄 Nb frappes reset (Service/Retour)");
      }
      
      if (newValue === "T" && updated.serviceReussite && updated.zoneCode) {
        updated.sousTypeCode = updated.zoneCode;
        console.log(`🔄 Retour avec zone service: T${updated.zoneCode}`);
      } else {
        updated.sousTypeCode = "";
        console.log("🔄 Sous-type reset (famille)");
      }
    }
    
    // DYNAMIQUE SERVICE → famille S + sousType = zone
    if (updated.serviceReussite && updated.zoneCode && !updated.familleCode) {
      updated.familleCode = "S";
      updated.sousTypeCode = updated.zoneCode;
      console.log(`🔄 Auto Service: S${updated.zoneCode}`);
    }
    
    // zone change → met à jour sousType SI service + famille S
    if (field === "zoneCode" && updated.serviceReussite && updated.familleCode === "S") {
      updated.sousTypeCode = newValue;
      console.log(`🔄 Zone ${newValue} → sousType S${newValue}`);
    }
    
    // zone change → met à jour sousType SI service + famille T (Retour)
    if (field === "zoneCode" && updated.serviceReussite && updated.familleCode === "T") {
      updated.sousTypeCode = newValue;
      console.log(`🔄 Zone ${newValue} → sousType T${newValue} (Retour)`);
    }
    
    // Double faute → Résultat = "Faute" automatiquement
    if (updated.serviceReussite === "SF") {
      updated.resultatCode = "F";
      console.log(`🔄 Double faute → Résultat = Faute`);
    }
    
    console.log("📋 pointForm:", updated);
    onChange(updated);
  };

  return (
    <div
      className="point-form"
      style={{
        marginBottom: "16px",
        backgroundColor: "#ffffff",
        borderRadius: "12px",
        padding: "10px 12px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        border: isPointLibre ? "1px solid #ff9800" : "1px solid #e0e0e0",
        position: "relative", // 🆕 bordure orange en mode point libre
      }}
    >
      {/* Bouton aide critères — collé dans le coin haut-gauche du bloc */}
      <button
        onClick={() => setShowHelp(true)}
        style={{
          position: 'absolute', top: '6px', left: '6px',
          width: '20px', height: '20px', borderRadius: '50%',
          border: 'none', backgroundColor: '#1976D2', color: 'white',
          fontSize: '11px', fontWeight: '700', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 1px 4px rgba(25,118,210,0.4)',
          zIndex: 10, lineHeight: 1,
        }}
        title="Aide sur les critères de saisie"
      >?</button>
      {showHelp && <CriteriaHelpModal onClose={() => setShowHelp(false)} />}

      {/* Famille */}
      <div className="pf-section" style={{ marginBottom: "8px" }}>
        <div
          className="pf-section-label"
          style={{
            fontSize: "12px",
            fontWeight: 600,
            marginBottom: "4px",
            color: "#555",
          }}
        >
          Famille
        </div>
        <div
          className="pf-buttons-row"
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "5px",
          }}
        >
          {refFamille.map((f) => (
            <ToggleButton
              key={f.code}
              active={pointForm.familleCode === f.code}
              onClick={() => updateField("familleCode", f.code)}
            >
              {f.labelFr}
            </ToggleButton>
          ))}
        </div>

        {/* 🆕 Message indicatif en mode point libre */}
        {isPointLibre && (
          <div style={{
            marginTop: "6px",
            fontSize: "11px",
            color: "#e65100",
            fontStyle: "italic",
          }}>
            Point libre — hors statistiques. Sélectionnez le joueur qui remporte le point.
          </div>
        )}
      </div>

      {/* Sous-type — masqué en mode point libre */}
      {!isPointLibre && (
        <div className="pf-section" style={{ marginBottom: "8px" }}>
          <div
            className="pf-section-label"
            style={{
              fontSize: "12px",
              fontWeight: 600,
              marginBottom: "4px",
              color: "#555",
            }}
          >
            Sous-type
          </div>
          <div
            className="pf-buttons-row"
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "5px",
            }}
          >
            {sousTypes.map((s) => (
              <ToggleButton
                key={s.code}
                active={pointForm.sousTypeCode === s.code}
                onClick={() => updateField("sousTypeCode", s.code)}
              >
                {s.labelFr}
              </ToggleButton>
            ))}
          </div>
        </div>
      )}

      {/* Nb frappes + Résultat + Plus */}
      <div
        className="pf-section"
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "5px",
          columnGap: "24px",
          marginBottom: "4px",
          alignItems: "flex-end",
        }}
      >
        {/* Nb frappes — masqué en mode point libre */}
        {!isPointLibre && (
          <div 
            className="pf-subsection" 
            style={{ 
              flex: "1 1 110px",
              visibility: ["S", "T"].includes(pointForm.familleCode) ? "hidden" : "visible"
            }}
          >
            <div
              className="pf-section-label"
              style={{
                fontSize: "12px",
                fontWeight: 600,
                marginBottom: "4px",
                color: "#555",
              }}
            >
              Nb frappes
            </div>
            <div
              className="pf-buttons-row"
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "5px",
              }}
            >
              {refNbFrappes.map((n) => (
                <ToggleButton key={n.code} active={pointForm.nbFrappesCode === n.code}
                onClick={() => updateField("nbFrappesCode", n.code)}
                >
                  {n.labelFr}
                </ToggleButton>
              ))}
            </div>
          </div>
        )}

        {/* Résultat — toujours visible, mais en mode point libre uniquement "Point" est disponible */}
        <div className="pf-subsection" style={{ flex: "0 0 auto" }}>
          <div
            className="pf-section-label"
            style={{
              fontSize: "12px",
              fontWeight: 600,
              marginBottom: "4px",
              color: "#555",
            }}
          >
            Résultat
          </div>
          <div
            className="pf-buttons-row"
            style={{
              display: "flex",
              gap: "10px",
            }}
          >
            {/* 🆕 En mode point libre, uniquement le bouton "Point" visible et actif */}
            {isPointLibre ? (
              <ToggleButton active={true} onClick={() => {}}>
                Point
              </ToggleButton>
            ) : (
              refResultat.map((r) => (
                <ToggleButton
                  key={r.code}
                  active={pointForm.resultatCode === r.code}
                  onClick={() => updateField("resultatCode", r.code)}
                >
                  {r.labelFr}
                </ToggleButton>
              ))
            )}
          </div>
        </div>

        {/* Plus — masqué en mode point libre */}
        {!isPointLibre && (
          <div
            className="pf-subsection"
            style={{
              flex: "0 0 auto",
              display: "flex",
              alignItems: "center",
            }}
          >
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                fontSize: "12px",
                color: "#555",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={showAdvanced}
                onChange={(e) => setShowAdvanced(e.target.checked)}
              />
              Plus
            </label>
          </div>
        )}
      </div>

      {/* Section avancée — masquée en mode point libre */}
      {!isPointLibre && showAdvanced && (
        <div
          className="pf-section"
          style={{
            marginTop: "8px",
            paddingTop: "8px",
            borderTop: "1px dashed #ddd",
            display: "flex",
            flexWrap: "wrap",
            gap: "12px",
            columnGap: "10px",
            width: "100%",
            justifyContent: "flex-start",
            fontSize: "12px",
            color: "#555",
          }}
        >
          {/* Services */}
          <div className="pf-subsection" style={{ flex: "0 0 auto" }}>
            <div
              className="pf-section-label"
              style={{
                fontSize: "12px",
                fontWeight: 600,
                marginBottom: "4px",
              }}
            >
              Services
            </div>
            <div
              className="pf-buttons-row"
              style={{
                display: "flex",
                flexDirection: "row",
                gap: "2px",
              }}
            >
              {refServiceReussite.map((s) => (
                <ToggleButton
                  key={s.code}
                  active={pointForm.serviceReussite === s.code}
                  onClick={() =>
                    updateField(
                      "serviceReussite",
                      pointForm.serviceReussite === s.code ? "" : s.code
                    )
                  }
                >
                  {s.labelFr}
                </ToggleButton>
              ))}
            </div>
          </div>

          {/* Effet */}
          <div className="pf-subsection" style={{ flex: "0 0 auto" }}>
            <div
              className="pf-section-label"
              style={{
                fontSize: "12px",
                fontWeight: 600,
                marginBottom: "4px",
              }}
            >
              Effet
            </div>
            <div
              className="pf-buttons-row"
              style={{
                display: "flex",
                flexDirection: "row",
                gap: "2px",
              }}
            >
              {refEffet.map((e) => (
                <ToggleButton
                  key={e.code}
                  active={pointForm.effetCode === e.code}
                  onClick={() =>
                    updateField(
                      "effetCode",
                      pointForm.effetCode === e.code ? "" : e.code
                    )
                  }
                >
                  {e.labelFr}
                </ToggleButton>
              ))}
            </div>
          </div>

          {/* Ligne Zone + Faute directe */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "8px",
              width: "100%",
            }}
          >
            {/* Zone */}
            <div className="pf-subsection" style={{ flex: "1 1 0" }}>
              <div
                className="pf-section-label"
                style={{
                  fontSize: "12px",
                  fontWeight: 600,
                  marginBottom: "4px",
                }}
              >
                Zone
              </div>
              <div
                className="pf-buttons-row"
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "4px",
                }}
              >
                {(dynamicZones || refZone).map((z) => (
                  <ToggleButton
                    key={z.code}
                    active={pointForm.zoneCode === z.code}
                    onClick={() =>
                      updateField(
                        "zoneCode",
                        pointForm.zoneCode === z.code ? "" : z.code
                      )
                    }
                  >
                    {z.labelFr}
                  </ToggleButton>
                ))}
              </div>
            </div>

            {/* Faute directe */}
            <div className="pf-subsection" style={{ flex: "1 1 0", minWidth: "160px" }}>
              <div
                className="pf-section-label"
                style={{
                  fontSize: "12px",
                  fontWeight: 600,
                  marginBottom: "4px",
                }}
              >
                Faute directe
              </div>
              <div
                className="pf-buttons-row"
                style={{
                  display: "flex",
                  flexDirection: "row",
                  gap: "4px",
                  flexWrap: "wrap",
                }}
              >
                {refFauteDirecte.map((f) => (
                  <ToggleButton
                    key={f.code}
                    active={pointForm.fauteDirecteCode === f.code}
                    onClick={() =>
                      updateField(
                        "fauteDirecteCode",
                        pointForm.fauteDirecteCode === f.code ? "" : f.code
                      )
                    }
                  >
                    {f.labelFr}
                  </ToggleButton>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PointForm;